import { useCallback, useEffect, useRef } from 'react';
import { throttle } from 'lodash';
import { MapView, FeatureLayer } from '../constructors';
import { ESRI_BATCHED_HIT_TEST_THROTTLE_THRESHOLD } from '../configurations';
import type { GraphicHit, ViewPointerMoveEvent } from '../types';

/**
 * Hook that performs a batched hit test on the map view to handle the hover of map elements.
 *
 * @param {MapView | undefined} mapView - The map view to perform the hit test on.
 * @param {FeatureLayer[]} layers - The layers to include in the hit test.
 * @returns {[Function, number | undefined]} - Returns a function that triggers the hit test
 * and the id of the graphic that was hit, or undefined if no graphic was hit.
 */
export const useBatchedHitTest = (mapView: MapView | undefined, layers: FeatureLayer[]) => {
    const onDragRef = useRef(false);
    const highlight = useRef<IHandle>();
    const highlightedObject = useRef<number>();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pointerMove = useCallback(
        throttle((event: MouseEvent | ViewPointerMoveEvent) => {
            if (onDragRef.current || !mapView) {
                return;
            }

            mapView
                .hitTest(event, { include: layers, exclude: mapView.graphics })
                .then((response) => {
                    const graphic = (response.results as GraphicHit[])[0]?.graphic;

                    // remove hover
                    if (
                        (!graphic || highlightedObject.current !== graphic.getObjectId()) &&
                        !!highlight.current
                    ) {
                        highlight.current.remove();
                        highlightedObject.current = undefined;
                    }

                    // apply hover
                    if (graphic && highlightedObject.current !== graphic.getObjectId()) {
                        mapView.whenLayerView(graphic.layer).then((layerView) => {
                            // @ts-expect-error
                            highlight.current = layerView.highlight(graphic);
                            highlightedObject.current = graphic.getObjectId();
                        });
                    }
                })
                .catch(() => {
                    // This gets rid of the geometry error that we have no control over:
                    // Cannot read properties of undefined (reading 'geometry')
                });
        }, ESRI_BATCHED_HIT_TEST_THROTTLE_THRESHOLD),
        [layers, mapView]
    );

    useEffect(() => {
        if (!mapView) {
            return;
        }

        const { remove: removeOnDrag } = mapView.on('drag', (event) => {
            if (event.action === 'start') {
                onDragRef.current = true;
            } else if (event.action === 'end') {
                onDragRef.current = false;
            }
        });

        const { remove } = mapView.on('pointer-move', (event) => {
            pointerMove(event);
        });

        return () => {
            remove();
            removeOnDrag();
        };
    }, [mapView, layers, pointerMove]);
};
